import type { IntlShape } from 'react-intl'
import {
  ESIM_UPSELL_TICKET_METADATA,
  type LoyaltyEsimUpsellKind,
  type LoyaltyEsimUpsellTicketMetadata,
} from 'loyalty/constants/loyaltyEsimUpsellOptions'
import {
  getEsimUpsellTicketMetadata,
  getEsimUpsellTicketIdsFromTicket,
} from 'loyalty/lib/esims/esimUpsellTicketUtils'
import { buildEsimUpsellPurchasableTickets } from 'loyalty/lib/esims/buildEsimUpsellPurchasableTickets'

/**
 * One selectable row in the eSIM data-upgrade modal.
 *
 * `isCurrent` rows are the member's active package (tier benefit or already-purchased upgrade)
 * and are built from static metadata without a catalog ticket.
 */
interface EsimUpsellPackageRow {
  ticketId: App.LoyaltyEsimUpsellTicketId
  kind: LoyaltyEsimUpsellKind
  ticket?: App.LoyaltyEsimUpsellCatalogTicket
  addonPrice: number
  badge: string | null
  title: string
  description: string
  isCurrent: boolean
}

function buildPurchasableUpsellPackageRow(
  intl: IntlShape,
  ticket: App.LoyaltyEsimUpsellCatalogTicket,
  upgrade: LoyaltyEsimUpsellTicketMetadata,
): EsimUpsellPackageRow {
  const badge = ticket.badgeMessageId === 'popular'
    ? intl.formatMessage({
        id: 'LP8kjPa_pWAeBAGGjmxh',
        defaultMessage: 'Popular upgrade',
        description: 'Badge label shown on a recommended paid eSIM upgrade option card in the modal',
      })
    : null
  const addonPrice = ticket.price

  if (upgrade.kind === 'gb_pack') {
    const currentPackage = getEsimUpsellTicketMetadata(ticket.bookingOptions.fromESimDataPackage)
    const currentGb = currentPackage.kind === 'gb_pack' ? currentPackage.gb : 0
    const targetGb = upgrade.gb
    const addedGb = Math.max(0, targetGb - currentGb)
    return {
      ticketId: upgrade.ticketId,
      kind: upgrade.kind,
      ticket,
      addonPrice,
      badge,
      isCurrent: false,
      title: intl.formatMessage(
        {
          id: 'lRV4_Cv-H41k67SUQ_vJ',
          defaultMessage: '+ {gb}GB',
          description: 'Primary label for a fixed-data eSIM package option in the upgrade modal (extra GB on top of the included tier benefit)',
        },
        { gb: addedGb },
      ),
      description: intl.formatMessage(
        {
          id: 'CCtYkqKQEdZL3GnBTf1_',
          defaultMessage: '{totalGb}GB total',
          description: 'Subtitle under a fixed-data eSIM upgrade option showing the total combined data',
        },
        { totalGb: targetGb },
      ),
    }
  }

  const days = upgrade.days
  return {
    ticketId: upgrade.ticketId,
    kind: upgrade.kind,
    ticket,
    addonPrice,
    badge,
    isCurrent: false,
    title: intl.formatMessage(
      {
        id: 'cZc1pnfvkx_x5APtYKiz',
        defaultMessage: '{days} days',
        description: 'Heading for an unlimited-data eSIM upgrade option in the modal (number of days)',
      },
      { days },
    ),
    description: intl.formatMessage({
      id: 'SmnN1gREI-gTv7_85GX9',
      defaultMessage: 'Unlimited data',
      description: 'Subtitle under an unlimited-data eSIM upgrade option in the modal',
    }),
  }
}

function buildCurrentPackageRow(
  intl: IntlShape,
  ticketId: App.LoyaltyEsimUpsellTicketId,
): EsimUpsellPackageRow {
  const metadata = ESIM_UPSELL_TICKET_METADATA[ticketId]
  const currentlyActiveDescription = intl.formatMessage({
    id: 'kdlMBC6aOTalpMGA8P3z',
    defaultMessage: 'Currently active',
    description: 'Subtitle under the user’s currently-active eSIM package in the upgrade modal',
  })

  if (metadata.kind === 'gb_pack') {
    const gb = metadata.gb
    return {
      ticketId,
      kind: metadata.kind,
      addonPrice: 0,
      badge: null,
      isCurrent: true,
      title: intl.formatMessage(
        {
          id: 'KWRMbDMw1ljte9gofgJ_',
          defaultMessage: '{gb}GB',
          description: 'Title for the user’s currently-active fixed-data eSIM package row in the upgrade modal',
        },
        { gb },
      ),
      description: currentlyActiveDescription,
    }
  }

  const days = metadata.days
  return {
    ticketId,
    kind: metadata.kind,
    addonPrice: 0,
    badge: null,
    isCurrent: true,
    title: intl.formatMessage(
      {
        id: 'P5izY2mOdQKrqpMXUS4C',
        defaultMessage: '{days} days',
        description: 'Title for the user’s currently-active unlimited-data eSIM package row in the upgrade modal',
      },
      { days },
    ),
    description: currentlyActiveDescription,
  }
}

function sortRowsByAddonPrice(rows: Array<EsimUpsellPackageRow>): Array<EsimUpsellPackageRow> {
  return [...rows].sort((a, b) => {
    if (a.addonPrice !== b.addonPrice) return a.addonPrice - b.addonPrice
    return a.ticketId.localeCompare(b.ticketId)
  })
}

/** Keeps the current package pinned to the top; remaining rows sort cheapest-first. */
function partitionPackageRowsByKind(
  rows: Array<EsimUpsellPackageRow>,
  kind: LoyaltyEsimUpsellKind,
): Array<EsimUpsellPackageRow> {
  const rowsForKind = rows.filter(row => row.kind === kind)
  const currentRow = rowsForKind.find(row => row.isCurrent)
  const purchasableRows = sortRowsByAddonPrice(rowsForKind.filter(row => !row.isCurrent))
  return currentRow ? [currentRow, ...purchasableRows] : purchasableRows
}

export function buildEsimUpsellPackageRows(
  intl: IntlShape,
  catalog: App.LoyaltyEsimUpsellCatalog,
  options: {
    currentPackageTicketId: App.LoyaltyEsimUpsellTicketId | null
  },
): {
  allPackageRows: Array<EsimUpsellPackageRow>
  purchasableRows: Array<EsimUpsellPackageRow>
  fixedDataRows: Array<EsimUpsellPackageRow>
  unlimitedDataRows: Array<EsimUpsellPackageRow>
} {
  const purchasableTickets = buildEsimUpsellPurchasableTickets(
    catalog,
    options.currentPackageTicketId,
  )

  const purchasableRows = purchasableTickets
    .map((ticket) => {
      const { toTicketId: ticketId } = getEsimUpsellTicketIdsFromTicket(ticket)
      if (!ticketId) return null
      const upgrade = ESIM_UPSELL_TICKET_METADATA[ticketId]
      return buildPurchasableUpsellPackageRow(intl, ticket, upgrade)
    })
    .filter((row): row is EsimUpsellPackageRow => row !== null)

  const allPackageRows = options.currentPackageTicketId
    ? [buildCurrentPackageRow(intl, options.currentPackageTicketId), ...purchasableRows]
    : purchasableRows

  return {
    allPackageRows,
    purchasableRows,
    fixedDataRows: partitionPackageRowsByKind(allPackageRows, 'gb_pack'),
    unlimitedDataRows: partitionPackageRowsByKind(allPackageRows, 'unlimited_days'),
  }
}
